// src/features/MediaLibrary/MediaPage.js - Unified and Optimized
import React, { useState, useCallback, useMemo } from 'react';
import MediaUpload from './MediaUpload';
import MediaBrowser from './MediaBrowser';
import AudioUpload from './AudioUpload';
import AudioBrowser from './AudioBrowser';
import './MediaPage.css';

const MediaPage = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('upload'); // FIXED: Start with upload
  const [mediaType, setMediaType] = useState('images');

  // OPTIMIZED: Use useCallback to prevent unnecessary re-renders
  const handleUploadComplete = useCallback(() => {
    // Force refresh of media browser when a new item is uploaded
    setRefreshKey(prev => prev + 1);
    // Stay on upload tab in case user wants to upload more
    // setActiveTab('browse'); // REMOVED: Don't auto-switch
  }, []);

  // OPTIMIZED: Memoize tab change handlers
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handleMediaTypeChange = useCallback((type) => {
    setMediaType(type);
    // Reset to upload when changing media type
    setActiveTab('upload');
  }, []);

  // OPTIMIZED: Memoize media type buttons
  const mediaTypeButtons = useMemo(() => (
    <div className="media-type-selector">
      <button
        className={`media-type-button ${mediaType === 'images' ? 'active' : ''}`}
        onClick={() => handleMediaTypeChange('images')}
      >
        📷 Images
      </button>
      <button
        className={`media-type-button ${mediaType === 'audio' ? 'active' : ''}`}
        onClick={() => handleMediaTypeChange('audio')}
      >
        🎵 Audio
      </button>
    </div>
  ), [mediaType, handleMediaTypeChange]);

  // OPTIMIZED: Memoize tab buttons  
  const tabButtons = useMemo(() => (
    <div className="tabs">
      <button
        className={`tab-button ${activeTab === 'upload' ? 'active' : ''}`}
        onClick={() => handleTabChange('upload')}
      >
        ⬆️ Upload New {mediaType === 'images' ? 'Image' : 'Audio'}
      </button>
      <button
        className={`tab-button ${activeTab === 'browse' ? 'active' : ''}`}
        onClick={() => handleTabChange('browse')}
      >
        📂 Browse {mediaType === 'images' ? 'Images' : 'Audio'}
      </button>
    </div>
  ), [activeTab, mediaType, handleTabChange]);

  // OPTIMIZED: Memoize tab content
  const tabContent = useMemo(() => {
    if (activeTab === 'upload') {
      return mediaType === 'images' ? (
        <MediaUpload onUploadComplete={handleUploadComplete} />
      ) : (
        <AudioUpload onUploadComplete={handleUploadComplete} />
      );
    }
    
    if (activeTab === 'browse') {
      return mediaType === 'images' ? (
        <MediaBrowser key={`img-${refreshKey}`} />
      ) : (
        <AudioBrowser key={`audio-${refreshKey}`} />
      );
    }
    
    return null;
  }, [activeTab, mediaType, refreshKey, handleUploadComplete]);

  return (
    <div className="media-page">
      <div className="page-header">
        <h2>Media Library</h2>
        <p>Upload and manage images and audio files for your lessons</p>
      </div>

      {mediaTypeButtons}
      {tabButtons}

      <div className="tab-content">
        {tabContent}
      </div>
    </div>
  );
};

export default React.memo(MediaPage);